// frontend/src/components/MusicSpectrogram.jsx
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import wsAction from '../utils/wsActions';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const POINTS_PER_SECOND = 200;

export default function MusicSpectrogram({
  music,
  user,
  sessionId,
  periods = [],
  currentPeriodIndex,
  onPeriodUpdate, // Callback pour notifier la mise à jour ET la sélection
  bottomBarHeight,
  controlBarHeight,
  currentTime, // Barre de progression placée par défaut au timestamp 0
  onTimeUpdate,
  audioRef={audioRef},
  onStopAnimation,
  wsSend, // WebSocket send function
  recordAction, // ✅ Pour undo/redo
  isInUndoRedo, // ✅ Pour vérifier si on est dans un undo/redo
}) {
  // Références vers les canevas
  const waveformCanvasRef = useRef(null);
  const timelineCanvasRef = useRef(null);
  const amplitudeSliderRef = useRef(null);
  const containerRef = useRef(null);

  // Références pour les overlays de la barre de progression
  const timelineProgressCanvasRef = useRef(null);
  const timelineWasDraggedRef = useRef(false);

  const waveformProgressCanvasRef = useRef(null);

  // États principaux
  const [waveformData, setWaveformData] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [viewWindow, setViewWindow] = useState({ start: 0, duration: 0 });
  const [amplitudeScale, setAmplitudeScale] = useState(1);
  const [dummyState, setDummyState] = useState(0);
  const [containerWidth, setContainerWidth] = useState(600);

  const [currentTimeLocal, setCurrentTimeLocal] = useState(currentTime);

  // Hauteur de la timeline et largeur de l'échelle d'amplitude
  const timelineHeight = 20;
  const amplitudeSliderWidth = 25;

  // MàJ de la largeur du conteneur (pour le positionnement)
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Flag pour indiquer qu'un drag d'édition d'extrémité vient de se terminer
  const edgeDragCompletedRef = useRef(false);

  // Stockage des dernières props dans une référence pour éviter des problèmes de fermeture
  const dataRef = useRef({ user, sessionId, periods, currentPeriodIndex, onPeriodUpdate, wsSend, recordAction, isInUndoRedo, currentTime, audioRef });
  useEffect(() => {
    dataRef.current = { user, sessionId, periods, currentPeriodIndex, onPeriodUpdate, wsSend, recordAction, isInUndoRedo, currentTime, audioRef };
    // Mise à jour des props:", dataRef.current);
  }, [user, sessionId, periods, currentPeriodIndex, onPeriodUpdate, wsSend, recordAction, isInUndoRedo, currentTime, audioRef]);

  // Pour le drag sur une extrémité de zone (édition d'une extrémité)
  const regionEdgeDragRef = useRef({
    dragging: false,
    regionId: null,
    edge: null, // "start" ou "end"
    initialTimestamp: 0,
    initialMouseX: 0,
    deltaTime: 0,
  });

  // Pour le pan de la waveform (déplacement de la vue)
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const initialStartRef = useRef(0);
  const draggedRef = useRef(false);

  // Pour le drag sur l'échelle d'amplitude
  const amplitudeDraggingRef = useRef(false);
  const amplitudeStartYRef = useRef(0);
  const amplitudeInitialScaleRef = useRef(1);

  // Fonction d'aide pour afficher en toute sécurité des nombres avec toFixed()
  const safeFixed = (num, digits) =>
    (typeof num === 'number' && !isNaN(num)) ? num.toFixed(digits) : num;

  /* =============================
     1. Récupération, décodage et extraction de la waveform
  ============================== */
  useEffect(() => {
    if (!music?.filename) return;

    let isMounted = true;
    const controller = new AbortController();
    const fetchAndDecode = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/media/${music.filename}`, {
          responseType: 'arraybuffer',
          signal: controller.signal,
        });

        const arrayBuffer = response.data;
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        if (!isMounted) return;

        setAudioDuration(audioBuffer.duration);
        const channelData = audioBuffer.getChannelData(0);
        const totalPoints = Math.floor(audioBuffer.duration * POINTS_PER_SECOND);
        const waveform = new Array(totalPoints).fill(0);
        const samplesPerPoint = Math.floor(channelData.length / totalPoints);
        for (let i = 0; i < totalPoints; i++) {
          const start = i * samplesPerPoint;
          const end = start + samplesPerPoint;
          let max = 0;
          for (let j = start; j < end && j < channelData.length; j++) {
            const absVal = Math.abs(channelData[j]);
            if (absVal > max) max = absVal;
          }
          waveform[i] = max;
        }

        setWaveformData(waveform);
      } catch (error) {
        if (!axios.isCancel(error)) console.error("[Fetch] Erreur lors du fetch/décodage:", error);
      }
    };
    fetchAndDecode();
    return () => {
      isMounted = false;
      controller.abort();

    };
  }, [music?.filename]);

  // Initialisation de la vue sur l'intégralité du signal
  useEffect(() => {
    if (audioDuration > 0) {

      setViewWindow({ start: 0, duration: audioDuration });
    }
  }, [audioDuration]);

  // Redessiner lors du redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      const canvas = timelineCanvasRef.current;
      if (!canvas || audioDuration === 0) return;
  
      const newWidth = canvas.parentElement ? canvas.parentElement.clientWidth : 600;
      const newDuration = (newWidth / canvas.width) * viewWindow.duration; // Ajustement à la nouvelle largeur
  
      setViewWindow((prev) => ({
        ...prev,
        duration: newDuration > audioDuration ? audioDuration : newDuration, // Assurer qu'on ne dépasse pas la durée audio
      }));
  
      setDummyState(Date.now());
    };
  
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [audioDuration, viewWindow]);

  /* =============================
     2. Dessin de la waveform dans le canevas principal
  ============================== */
  useEffect(() => {
    if (!waveformData || audioDuration === 0 || viewWindow.duration === 0) return;
    const canvas = waveformCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const containerWidth = canvas.parentElement ? canvas.parentElement.clientWidth : 600;
    canvas.width = containerWidth - amplitudeSliderWidth;
    canvas.height = bottomBarHeight - timelineHeight - controlBarHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#efefef';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const width = canvas.width;
    const height = canvas.height;
    const centerY = height / 2;

    const totalPoints = waveformData.length;
    const viewStartTime = viewWindow.start;
    const viewEndTime = viewWindow.start + viewWindow.duration;
    const startIndex = Math.floor((viewStartTime / audioDuration) * totalPoints);
    const endIndex = Math.floor((viewEndTime / audioDuration) * totalPoints);
    const visibleWaveform = waveformData.slice(startIndex, Math.max(endIndex, startIndex + 1));
    const Nvisible = visibleWaveform.length;
    let displayData, numPoints;
    if (Nvisible > width) {
      numPoints = width;
      displayData = new Array(numPoints);
      for (let i = 0; i < numPoints; i++) {
        const idxStart = Math.floor((i / numPoints) * Nvisible);
        const idxEnd = Math.floor(((i + 1) / numPoints) * Nvisible);
        let maxVal = 0;
        for (let j = idxStart; j < idxEnd; j++) {
          if (visibleWaveform[j] > maxVal) maxVal = visibleWaveform[j];
        }
        displayData[i] = maxVal;
      }
    } else {
      numPoints = Nvisible;
      displayData = visibleWaveform;
    }

    const heightReductionFactor = 0.8;
    ctx.beginPath();
    for (let i = 0; i < numPoints; i++) {
      const x = numPoints > 1 ? (i / (numPoints - 1)) * width : width / 2;
      const amplitude = displayData[i];
      const yTop = centerY - amplitude * amplitudeScale * centerY * heightReductionFactor;
      if (i === 0) ctx.moveTo(x, yTop);
      else ctx.lineTo(x, yTop);
    }
    for (let i = numPoints - 1; i >= 0; i--) {
      const x = numPoints > 1 ? (i / (numPoints - 1)) * width : width / 2;
      const amplitude = displayData[i];
      const yBottom = centerY + amplitude * amplitudeScale * centerY * heightReductionFactor;
      ctx.lineTo(x, yBottom);
    }
    ctx.closePath();
    ctx.fillStyle = '#999';
    ctx.fill();

    // Affichage des régions (zones) en rouge
    periods.forEach((period) => {
      if (period.start_time == null || period.end_time == null) return;
      if (period.end_time < viewWindow.start || period.start_time > viewWindow.start + viewWindow.duration)
        return;
      let regionStartTime = period.start_time;
      let regionEndTime = period.end_time;
      if (
        regionEdgeDragRef.current.regionId === period.id &&
        regionEdgeDragRef.current.dragging
      ) {
        if (regionEdgeDragRef.current.edge === 'start') {
          regionStartTime = regionEdgeDragRef.current.initialTimestamp + regionEdgeDragRef.current.deltaTime;
        } else if (regionEdgeDragRef.current.edge === 'end') {
          regionEndTime = regionEdgeDragRef.current.initialTimestamp + regionEdgeDragRef.current.deltaTime;
        }
      }
      regionStartTime = Math.max(regionStartTime, viewWindow.start);
      regionEndTime = Math.min(regionEndTime, viewWindow.start + viewWindow.duration);
      const xStart = ((regionStartTime - viewWindow.start) / viewWindow.duration) * width;
      const xEnd = ((regionEndTime - viewWindow.start) / viewWindow.duration) * width;
      const regionWidth = xEnd - xStart;
      const isCurrent = period.id === dataRef.current.periods[dataRef.current.currentPeriodIndex]?.id;
      const fillColor = isCurrent ? 'rgba(255, 0, 0, 0.5)' : 'rgba(255, 0, 0, 0.3)';
      ctx.fillStyle = fillColor;
      ctx.fillRect(xStart, 0, regionWidth, height);
      ctx.lineWidth = isCurrent ? 3 : 1;
      ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
      ctx.strokeRect(xStart, 0, regionWidth, height);
    });

    // La barre de progression a été retirée de ce canvas (elle sera gérée via un overlay)
  }, [waveformData, periods, bottomBarHeight, audioDuration, viewWindow, amplitudeScale, dummyState, currentPeriodIndex, currentTime, onTimeUpdate]);

  /* =============================
     3. Dessin de la timeline (échelle temporelle) en haut
  ============================== */
  useEffect(() => {
    const canvas = timelineCanvasRef.current;
    if (!canvas || audioDuration === 0 || viewWindow.duration === 0) return;
    //console.log("[Timeline] Dessin de la timeline avec viewWindow =", viewWindow);
    const ctx = canvas.getContext('2d');
    const containerWidth = canvas.parentElement ? canvas.parentElement.clientWidth : 600;
    canvas.width = containerWidth;
    canvas.height = timelineHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#efefef';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, timelineHeight - 1);
    ctx.lineTo(canvas.width, timelineHeight - 1);
    ctx.stroke();

    const desiredTickCount = Math.floor(canvas.width / 50);
    const rawTickInterval = viewWindow.duration / desiredTickCount;
    const getNiceTickInterval = (rawInterval) => {
      const exponent = Math.floor(Math.log10(rawInterval));
      const fraction = rawInterval / Math.pow(10, exponent);
      let niceFraction;
      if (fraction < 1.5) niceFraction = 1;
      else if (fraction < 3) niceFraction = 2;
      else if (fraction < 7) niceFraction = 5;
      else niceFraction = 10;
      return niceFraction * Math.pow(10, exponent);
    };
    const tickInterval = getNiceTickInterval(rawTickInterval);
    const firstVisibleTick = Math.floor(viewWindow.start / tickInterval) * tickInterval;

    //console.log(`[Timeline] Intervalle des ticks = ${tickInterval}s, démarrage à ${firstVisibleTick}s`);
    for (let tick = firstVisibleTick; tick <= viewWindow.start + viewWindow.duration; tick += tickInterval) {
      const x = ((tick - viewWindow.start) / viewWindow.duration) * canvas.width;
      
      ctx.beginPath();
      ctx.moveTo(x, timelineHeight - 1);
      ctx.lineTo(x, timelineHeight - 10);
      ctx.stroke();

      ctx.fillStyle = '#333';
      ctx.font = '10px sans-serif';
      const tickLabel = tick % 1 === 0 ? tick.toFixed(0) + 's' : tick.toFixed(1) + 's';
      ctx.fillText(tickLabel, x + 2, 10);

      const subTickInterval = tickInterval / 5;
      for (let subTick = tick + subTickInterval; subTick < tick + tickInterval; subTick += subTickInterval) {
        if (subTick > viewWindow.start + viewWindow.duration) break;
        const subX = ((subTick - viewWindow.start) / viewWindow.duration) * canvas.width;
        ctx.beginPath();
        ctx.moveTo(subX, timelineHeight - 1);
        ctx.lineTo(subX, timelineHeight - 5);
        ctx.stroke();
      }
    }
    
    // La barre de progression a été retirée de ce canvas (elle sera gérée via un overlay)
  }, [viewWindow, audioDuration, currentTime, onTimeUpdate]);

  /* =============================
     4. Gestion du drag & drop sur la waveform
  ============================== */
  const getHoveredRegionEdge = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const threshold = 5; // pixels
    let result = null;
    dataRef.current.periods.forEach((region) => {
      if (region.start_time == null || region.end_time == null) return;
      if (region.end_time < viewWindow.start || region.start_time > viewWindow.start + viewWindow.duration)
        return;
      const regionStart = Math.max(region.start_time, viewWindow.start);
      const regionEnd = Math.min(region.end_time, viewWindow.start + viewWindow.duration);
      const xStart = ((regionStart - viewWindow.start) / viewWindow.duration) * rect.width;
      const xEnd = ((regionEnd - viewWindow.start) / viewWindow.duration) * rect.width;
      if (Math.abs(mouseX - xStart) < threshold) {
        result = { regionId: region.id, edge: 'start', timestamp: region.start_time };
      } else if (Math.abs(mouseX - xEnd) < threshold) {
        result = { regionId: region.id, edge: 'end', timestamp: region.end_time };
      }
    });
    return result;
  };

  useEffect(() => {
    const canvas = waveformCanvasRef.current;
    if (!canvas || audioDuration === 0) return;
  
    const handleMouseDown = (e) => {
      const edgeInfo = getHoveredRegionEdge(e, canvas);
      if (edgeInfo) {
        regionEdgeDragRef.current = {
          dragging: false,
          regionId: edgeInfo.regionId,
          edge: edgeInfo.edge,
          initialTimestamp: edgeInfo.timestamp,
          initialMouseX: e.clientX,
          deltaTime: 0,
        };
        return;
      }
      isDraggingRef.current = true;
      draggedRef.current = false;
      dragStartXRef.current = e.clientX;
      initialStartRef.current = viewWindow.start;
    };
  
    const handleMouseMove = (e) => {
      if (regionEdgeDragRef.current.regionId && !regionEdgeDragRef.current.dragging) {
        const deltaX = e.clientX - regionEdgeDragRef.current.initialMouseX;
        if (Math.abs(deltaX) > 5) {
          regionEdgeDragRef.current.dragging = true;
          canvas.style.cursor = 'text';
        }
      }
      if (regionEdgeDragRef.current.dragging) {
        const deltaX = e.clientX - regionEdgeDragRef.current.initialMouseX;
        regionEdgeDragRef.current.deltaTime = (deltaX / canvas.width) * viewWindow.duration;
        setDummyState(Date.now());
        return;
      }
      if (!isDraggingRef.current) {
        const edgeInfo = getHoveredRegionEdge(e, canvas);
        canvas.style.cursor = edgeInfo ? 'text' : 'pointer';
        return;
      }
      const deltaX = e.clientX - dragStartXRef.current;
      if (Math.abs(deltaX) > 5) draggedRef.current = true;
      let newStart = initialStartRef.current - (deltaX / canvas.width) * viewWindow.duration;
      newStart = Math.max(0, Math.min(newStart, audioDuration - viewWindow.duration));
      setViewWindow({ ...viewWindow, start: newStart });
    };
  
    const handleMouseUp = (e) => {
      if (regionEdgeDragRef.current.regionId) {
        if (regionEdgeDragRef.current.dragging) {
          const { regionId, edge, deltaTime } = regionEdgeDragRef.current;
          const region = dataRef.current.periods.find((r) => r.id === regionId);
          if (region) {
            const minDuration = 1;
            let newStart = region.start_time;
            let newEnd = region.end_time;
            const sortedOtherPeriods = dataRef.current.periods
              .filter((r) => r.id !== regionId)
              .sort((a, b) => a.start_time - b.start_time);
  
            if (edge === 'start') {
              newStart = Math.max(0, region.start_time + deltaTime);
              let indexCurrent = sortedOtherPeriods.findIndex((r) => r.start_time >= region.start_time);
              if (indexCurrent === -1) {
                indexCurrent = sortedOtherPeriods.length;
              }
              const prevPeriod = indexCurrent > 0 ? sortedOtherPeriods[indexCurrent - 1] : null;
              
              if (prevPeriod) newStart = Math.max(newStart, prevPeriod.end_time);
              newStart = Math.min(newStart, region.end_time - minDuration);

              // ✅ Enregistrer l'action pour undo/redo (seulement si pas déjà en undo/redo)
              const oldStart = region.start_time;
              const oldEnd = region.end_time;
              const savedTime = dataRef.current.audioRef?.current?.currentTime ?? dataRef.current.currentTime ?? 0;
              const currentPeriodId = dataRef.current.periods[dataRef.current.currentPeriodIndex]?.id;
              if (dataRef.current.recordAction && !dataRef.current.isInUndoRedo?.()) {
                const undoData = {
                  actionType: 'period_update',
                  data: { period_id: regionId, start_time: oldStart, end_time: oldEnd, _restore_time: savedTime, _restore_period_id: currentPeriodId }
                };
                const redoData = {
                  actionType: 'period_update',
                  data: { period_id: regionId, start_time: newStart, end_time: oldEnd, _restore_time: newStart, _restore_period_id: regionId }
                };
                dataRef.current.recordAction('period_update', undoData, redoData, 'Modification du début de période');
              }

              wsAction.updatePeriod(
                dataRef.current.wsSend,
                { period_id: regionId, start_time: newStart, end_time: region.end_time },
                dataRef.current.user.access_token
              )
              .then(() => {
                dataRef.current.onPeriodUpdate({
                  update: true,
                  periodId: regionId,
                  newStart,
                  newEnd: region.end_time,
                });
              })
              .catch((error) => {});
            } else if (edge === 'end') {
              newEnd = Math.min(region.end_time + deltaTime, audioDuration);
              const indexCurrent = sortedOtherPeriods.findIndex((r) => r.start_time > region.end_time);
              const nextPeriod = indexCurrent !== -1 ? sortedOtherPeriods[indexCurrent] : null;
              if (nextPeriod) newEnd = Math.min(newEnd, nextPeriod.start_time);
              newEnd = Math.max(newEnd, region.start_time + minDuration);

              // ✅ Enregistrer l'action pour undo/redo (seulement si pas déjà en undo/redo)
              const oldStart = region.start_time;
              const oldEnd = region.end_time;
              const savedTime = dataRef.current.audioRef?.current?.currentTime ?? dataRef.current.currentTime ?? 0;
              const currentPeriodId = dataRef.current.periods[dataRef.current.currentPeriodIndex]?.id;
              if (dataRef.current.recordAction && !dataRef.current.isInUndoRedo?.()) {
                const undoData = {
                  actionType: 'period_update',
                  data: { period_id: regionId, start_time: oldStart, end_time: oldEnd, _restore_time: savedTime, _restore_period_id: currentPeriodId }
                };
                const redoData = {
                  actionType: 'period_update',
                  data: { period_id: regionId, start_time: oldStart, end_time: newEnd, _restore_time: newEnd, _restore_period_id: regionId }
                };
                dataRef.current.recordAction('period_update', undoData, redoData, 'Modification de la fin de période');
              }

              wsAction.updatePeriod(
                dataRef.current.wsSend,
                { period_id: regionId, start_time: region.start_time, end_time: newEnd },
                dataRef.current.user.access_token
              )
              .then(() => {
                dataRef.current.onPeriodUpdate({
                  update: true,
                  periodId: regionId,
                  newStart: region.start_time,
                  newEnd,
                });
              })
              .catch((error) => {});
            }
          }
          edgeDragCompletedRef.current = true;
          regionEdgeDragRef.current = {
            dragging: false,
            regionId: null,
            edge: null,
            initialTimestamp: 0,
            initialMouseX: 0,
            deltaTime: 0,
          };
          return;
        } else {
          const rect = canvas.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const clickTime = viewWindow.start + (clickX / rect.width) * viewWindow.duration;
          const clickedIndex = dataRef.current.periods.findIndex(
            (region) => clickTime >= region.start_time && clickTime <= region.end_time
          );
          if (clickedIndex !== -1) {
            dataRef.current.onPeriodUpdate({ select: true, periodIndex: clickedIndex });
          }
          regionEdgeDragRef.current = {
            dragging: false,
            regionId: null,
            edge: null,
            initialTimestamp: 0,
            initialMouseX: 0,
            deltaTime: 0,
          };
          return;
        }
      }
  
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
      }
    };
  
    canvas.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [viewWindow, audioDuration]);
  
  /* =============================
     5. Gestion du drag sur la timeline pour zoomer/dézoomer
        Gestion du clic sur la timeline pour placer l'avancement
  ============================== */
  const timelineDraggingRef = useRef(false);
  const timelineDragStartXRef = useRef(0);
  const timelineInitialViewWindowRef = useRef({ start: 0, duration: 0 });
  useEffect(() => {
    const canvas = timelineCanvasRef.current;
    if (!canvas || audioDuration === 0) return;
    const handleTimelineMouseDown = (e) => {
      timelineDraggingRef.current = true;
      timelineWasDraggedRef.current = false;
      timelineDragStartXRef.current = e.clientX;
      timelineInitialViewWindowRef.current = { ...viewWindow };
      //console.log(`[Timeline] Démarrage du zoom/pan sur la timeline: viewWindow initial = ${JSON.stringify(viewWindow)}`);
    };
    const handleTimelineMouseMove = (e) => {
      if (!timelineDraggingRef.current) return;
      const deltaX = e.clientX - timelineDragStartXRef.current;
      if (Math.abs(deltaX) > 5) {
        timelineWasDraggedRef.current = true;
      }
      const timelineSensitivity = 0.15;
      let newDuration = timelineInitialViewWindowRef.current.duration - deltaX * timelineSensitivity;
      const minDuration = 1;
      if (newDuration < minDuration) newDuration = minDuration;
      if (newDuration > audioDuration - timelineInitialViewWindowRef.current.start) {
        newDuration = audioDuration - timelineInitialViewWindowRef.current.start;
      }
      setViewWindow({ start: timelineInitialViewWindowRef.current.start, duration: newDuration });
    };
    const handleTimelineMouseUp = () => {
      timelineDraggingRef.current = false;
      //console.log("[Timeline] Fin du zoom/pan sur la timeline");
    };
    canvas.addEventListener('mousedown', handleTimelineMouseDown);
    window.addEventListener('mousemove', handleTimelineMouseMove);
    window.addEventListener('mouseup', handleTimelineMouseUp);
    return () => {
      canvas.removeEventListener('mousedown', handleTimelineMouseDown);
      window.removeEventListener('mousemove', handleTimelineMouseMove);
      window.removeEventListener('mouseup', handleTimelineMouseUp);
    };
  }, [viewWindow, audioDuration]);

  const handleTimelineClick = (e) => {
    // Si un drag a été détecté, on réinitialise le flag et on ne fait rien
    if (timelineWasDraggedRef.current) {
      timelineWasDraggedRef.current = false;
      return;
    }

    const rect = timelineCanvasRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = viewWindow.start + (clickX / rect.width) * viewWindow.duration;
    //console.log(`[Click] Placement de la barre de progression à ${newTime.toFixed(2)}s`);
  
    // Notifier le parent du nouveau temps via le callback onTimeUpdate
    if (typeof onTimeUpdate === 'function') {
      onTimeUpdate(newTime);
    }
  


    // Si vous passez également une référence audio, vous pouvez la mettre à jour ici :
    if (audioRef && audioRef.current) {
      audioRef.current.currentTime = newTime;
    }

          
    if (typeof onStopAnimation === 'function') {
      //console.log("[Click] Arrêt de l'animation au temps ", newTime);
      onStopAnimation(newTime);
    }
  };

  useEffect(() => {
    setCurrentTimeLocal(currentTime);
  }, [currentTime]);
  
  
  /* =============================
     6. Zoom avec la molette sur la waveform
  ============================== */
  useEffect(() => {
    const canvas = waveformCanvasRef.current;
    if (!canvas || audioDuration === 0) return;
    const handleWheel = (e) => {
      e.preventDefault();
      const zoomScale = e.deltaY < 0 ? 0.9 : 1.1;
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const currentStart = viewWindow.start;
      const currentDuration = viewWindow.duration;
      const mouseTime = currentStart + (mouseX / rect.width) * currentDuration;
      let newDuration = currentDuration * zoomScale;
      const minDuration = 1;
      if (newDuration < minDuration) newDuration = minDuration;
      if (newDuration > audioDuration - currentStart) newDuration = audioDuration - currentStart;
      let newStart = mouseTime - (mouseX / rect.width) * newDuration;
      if (newStart < 0) newStart = 0;
      if (newStart + newDuration > audioDuration) newStart = audioDuration - newDuration;
      setViewWindow({ start: newStart, duration: newDuration });
    };
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [viewWindow, audioDuration]);

  /* =============================
     7. Gestion du drag sur l'échelle d'amplitude pour ajuster amplitudeScale
  ============================== */
  useEffect(() => {
    const slider = amplitudeSliderRef.current;
    if (!slider) return;
    const minAmplitude = 0.5;
    const maxAmplitude = 3;
    const handleAmplitudeMouseDown = (e) => {
      amplitudeDraggingRef.current = true;
      amplitudeStartYRef.current = e.clientY;
      amplitudeInitialScaleRef.current = amplitudeScale;

      e.stopPropagation();
      e.preventDefault();
    };
    const handleAmplitudeMouseMove = (e) => {
      if (!amplitudeDraggingRef.current) return;
      const deltaY = e.clientY - amplitudeStartYRef.current;
      const sliderHeight = slider.clientHeight;
      let newScale = amplitudeInitialScaleRef.current - (deltaY / sliderHeight) * (maxAmplitude - minAmplitude);
      if (newScale < minAmplitude) newScale = minAmplitude;
      if (newScale > maxAmplitude) newScale = maxAmplitude;

      setAmplitudeScale(newScale);
    };
    const handleAmplitudeMouseUp = () => {
      if (amplitudeDraggingRef.current) {

      }
      amplitudeDraggingRef.current = false;
    };
    slider.addEventListener('mousedown', handleAmplitudeMouseDown);
    window.addEventListener('mousemove', handleAmplitudeMouseMove);
    window.addEventListener('mouseup', handleAmplitudeMouseUp);
    return () => {
      slider.removeEventListener('mousedown', handleAmplitudeMouseDown);
      window.removeEventListener('mousemove', handleAmplitudeMouseMove);
      window.removeEventListener('mouseup', handleAmplitudeMouseUp);
    };
  }, [amplitudeScale]);

  /* =============================
     8. Dessin de l'échelle d'amplitude sur le canevas
  ============================== */
  useEffect(() => {
    const slider = amplitudeSliderRef.current;
    if (!slider) return;
    const ctx = slider.getContext('2d');
    const width = slider.width = slider.clientWidth;
    const height = slider.height = slider.clientHeight;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#efefef';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#333';

    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, height);
    ctx.stroke();

    const minA = -1;
    const maxA = 1;
    const tickInterval = 0.5;
    ctx.fillStyle = '#333';
    ctx.font = '10px sans-serif';

    for (let value = minA; value <= maxA; value += tickInterval) {
      const padding = 10;
      const y = padding + ((maxA - value) / (maxA - minA)) * (height - 2 * padding);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(5, y);
      ctx.stroke();
      let absoluteValue = Math.abs(value);
      ctx.fillText(safeFixed(absoluteValue, 1), 7, y + 3);
    }

  }, [amplitudeScale, bottomBarHeight]);

  /* =============================
     9. Gestion du clic sur la waveform (sans drag)
  ============================== */
  const handleWaveformClick = (e) => {
    if (edgeDragCompletedRef.current) {
      edgeDragCompletedRef.current = false;
      return;
    }
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    const canvas = waveformCanvasRef.current;
    if (!canvas || audioDuration === 0) return;
    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickTime = viewWindow.start + (clickX / rect.width) * viewWindow.duration;
    const clickedIndex = dataRef.current.periods.findIndex(region =>
      clickTime >= region.start_time && clickTime <= region.end_time
    );
    if (clickedIndex !== -1) {
      const period = dataRef.current.periods[clickedIndex];
      // Mise à jour de la barre de progression : positionne l'audio au début de la période
      if (audioRef && audioRef.current) {
        audioRef.current.currentTime = period.start_time;
      }
      if (typeof onTimeUpdate === 'function') {
        onTimeUpdate(period.start_time);
      }
      // Notifie le parent pour la sélection de la période
      dataRef.current.onPeriodUpdate({ select: true, periodIndex: clickedIndex });
      return;
    }
    const { currentPeriodIndex, periods, user, sessionId } = dataRef.current;
    if (!user?.access_token) return;
    if (!periods[currentPeriodIndex]) {
      const DEFAULT_PERIOD_DURATION = 2;
      let newStart = clickTime;
      let newEnd = newStart + DEFAULT_PERIOD_DURATION;
      if (newEnd > audioDuration) {
        newEnd = audioDuration;
        newStart = Math.max(0, newEnd - DEFAULT_PERIOD_DURATION);
      }
      const sortedPeriods = [...periods].sort((a, b) => a.start_time - b.start_time);
      sortedPeriods.forEach(period => {
        if (newStart >= period.start_time && newStart < period.end_time) {
          newStart = period.end_time;
          newEnd = newStart + DEFAULT_PERIOD_DURATION;
        }
      });
      if (newEnd > audioDuration) {
        newEnd = audioDuration;
        newStart = Math.max(0, newEnd - DEFAULT_PERIOD_DURATION);
      }
      const nextPeriod = sortedPeriods.find(period => period.start_time < newEnd && period.start_time >= newStart);
      if (nextPeriod) {
        const candidateStart = nextPeriod.start_time - DEFAULT_PERIOD_DURATION;
        const previousPeriod = sortedPeriods.filter(period => period.end_time <= candidateStart).pop();
        if (!previousPeriod || candidateStart >= previousPeriod.end_time) {
          newStart = candidateStart;
          newEnd = nextPeriod.start_time;
        } else {
          newEnd = nextPeriod.start_time;
          newStart = newEnd - DEFAULT_PERIOD_DURATION;
          if (newStart < (previousPeriod ? previousPeriod.end_time : 0)) {
            console.error("Espace insuffisant pour créer une nouvelle période de 2 secondes sans chevauchement.");
            return;
          }
        }
      }
      wsAction.createPeriod(
        dataRef.current.wsSend,
        { session_id: sessionId, start_time: newStart, end_time: newEnd },
        user.access_token
      )
        .then((response) => {
          dataRef.current.onPeriodUpdate({ add: true, period: response });
        })
        .catch((error) => {
          console.error("[Click] Erreur lors de la création de la période :", error);
        });
      return;
    }
    const currentPeriod = periods[currentPeriodIndex];
    const newDuration = currentPeriod.end_time - currentPeriod.start_time;
    let newStart = clickTime;
    let newEnd = newStart + newDuration;
    const sortedPeriodsForUpdate = periods.filter(p => p.id !== currentPeriod.id).sort((a, b) => a.start_time - b.start_time);
    let newIdx = sortedPeriodsForUpdate.findIndex(r => r.start_time > newStart);
    if (newIdx > 0) {
      const prevPeriod = sortedPeriodsForUpdate[newIdx - 1];
      if (newStart < prevPeriod.end_time) {
        newStart = prevPeriod.end_time;
        newEnd = newStart + newDuration;
      }
    }
    newIdx = sortedPeriodsForUpdate.findIndex(r => r.start_time > newStart);
    if (newIdx === -1) newIdx = sortedPeriodsForUpdate.length;
    if (newIdx < sortedPeriodsForUpdate.length) {
      const nextPeriod = sortedPeriodsForUpdate[newIdx];
      if (newEnd > nextPeriod.start_time) {
        newEnd = nextPeriod.start_time;
      }
    }
    if (newEnd > audioDuration) {
      newEnd = audioDuration;
    }
    
    // ✅ Enregistrer l'action pour undo/redo (seulement si pas déjà en undo/redo)
    const oldStart = currentPeriod.start_time;
    const oldEnd = currentPeriod.end_time;
    const savedTime = audioRef?.current?.currentTime ?? currentTime ?? 0;
    const selectedPeriodId = periods[currentPeriodIndex]?.id;
    if (recordAction && !isInUndoRedo?.()) {
      const undoData = {
        actionType: 'period_update',
        data: { period_id: currentPeriod.id, start_time: oldStart, end_time: oldEnd, _restore_time: savedTime, _restore_period_id: selectedPeriodId }
      };
      const redoData = {
        actionType: 'period_update',
        data: { period_id: currentPeriod.id, start_time: newStart, end_time: newEnd, _restore_time: newStart, _restore_period_id: currentPeriod.id }
      };
      recordAction('period_update', undoData, redoData, 'Déplacement de période');
    }
    
    wsAction.updatePeriod(
      dataRef.current.wsSend,
      { period_id: currentPeriod.id, start_time: newStart, end_time: newEnd },
      user.access_token
    )
      .then(() => {
        dataRef.current.onPeriodUpdate({ update: true, periodId: currentPeriod.id, newStart, newEnd });
      })
      .catch((error) => {
        console.error("[Click] Erreur lors de la mise à jour de la région :", error);
      });
  };

  /* =============================
     10. Mise à jour séparée des overlays de la barre de progression
  ============================== */
  useEffect(() => {
    let animationFrameId;
  
    const renderTimeline = () => {
      const baseCanvas = timelineCanvasRef.current;
      const overlay = timelineProgressCanvasRef.current;
      if (!baseCanvas || !overlay) {
        animationFrameId = requestAnimationFrame(renderTimeline);
        return;
      }
      overlay.width = baseCanvas.width;
      overlay.height = baseCanvas.height;
      const ctx = overlay.getContext('2d');
      ctx.clearRect(0, 0, overlay.width, overlay.height);
  
      // Lire directement la valeur de l'élément audio
      const currentTimeLocal = audioRef.current ? audioRef.current.currentTime : 0;
      if (currentTimeLocal >= viewWindow.start && currentTimeLocal <= viewWindow.start + viewWindow.duration) {
        const xProgress = ((currentTimeLocal - viewWindow.start) / viewWindow.duration) * overlay.width;
        ctx.beginPath();
        ctx.moveTo(xProgress, 0);
        ctx.lineTo(xProgress, overlay.height);
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#000000';
        ctx.stroke();
  
        // Dessiner le triangle orienté vers le bas
        const triangleWidth = 10;
        const triangleHeight = 10;
        ctx.beginPath();
        ctx.moveTo(xProgress, triangleHeight);
        ctx.lineTo(xProgress - triangleWidth / 2, 0);
        ctx.lineTo(xProgress + triangleWidth / 2, 0);
        ctx.closePath();
        ctx.fillStyle = '#000000';
        ctx.fill();
      }
      animationFrameId = requestAnimationFrame(renderTimeline);
    };
  
    renderTimeline();
    return () => cancelAnimationFrame(animationFrameId);
  }, [viewWindow, audioDuration]);
  

  useEffect(() => {
    let animationFrameId;
  
    const renderWaveformProgress = () => {
      const baseCanvas = waveformCanvasRef.current;
      const overlay = waveformProgressCanvasRef.current;
      if (!baseCanvas || !overlay) {
        animationFrameId = requestAnimationFrame(renderWaveformProgress);
        return;
      }
      overlay.width = baseCanvas.width;
      overlay.height = baseCanvas.height;
      const ctx = overlay.getContext('2d');
      ctx.clearRect(0, 0, overlay.width, overlay.height);
  
      // Lire directement la valeur de l'élément audio
      const currentTimeLocal = audioRef.current ? audioRef.current.currentTime : 0;
      if (currentTimeLocal >= viewWindow.start && currentTimeLocal <= viewWindow.start + viewWindow.duration) {
        const xProgress = ((currentTimeLocal - viewWindow.start) / viewWindow.duration) * overlay.width;
        ctx.beginPath();
        ctx.moveTo(xProgress, 0);
        ctx.lineTo(xProgress, overlay.height);
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#000000';
        ctx.stroke();
      }
      animationFrameId = requestAnimationFrame(renderWaveformProgress);
    };
  
    renderWaveformProgress();
    return () => cancelAnimationFrame(animationFrameId);
  }, [viewWindow, audioDuration, waveformData, bottomBarHeight, controlBarHeight]);
  


  /* =============================
     11. Rendu du composant
  ============================== */
  useEffect(() => {
    const handleResize = () => {
      setDummyState(Date.now());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative', userSelect: 'none' }}>
      {/* Timeline */}
      <canvas
        ref={timelineCanvasRef}
        onClick={handleTimelineClick}
        style={{
          width: `calc(100% - ${amplitudeSliderWidth}px)`,
          height: `${timelineHeight}px`,
          display: 'block',
          cursor: 'ew-resize',
        }}
      />
      {/* Overlay pour la barre de progression de la timeline */}
      <canvas
        ref={timelineProgressCanvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          width: `calc(100% - ${amplitudeSliderWidth}px)`,
          height: `${timelineHeight}px`,
        }}
      />
      <div style={{ display: 'flex' }}>
        {/* Zone centrale : Affiche la waveform si chargée, sinon un message de chargement */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          {!waveformData ? (
            <div
              style={{
                fontSize: '16px',
                color: '#555',
                backgroundColor: '#efefef',
                textAlign: 'center',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Waveform en cours de chargement...
            </div>
          ) : (
            <>
              <canvas
                ref={waveformCanvasRef}
                onClick={handleWaveformClick}
                style={{ width: '100%', cursor: 'pointer' }}
              />
              {/* Overlay pour la barre de progression de la waveform */}
              <canvas
                ref={waveformProgressCanvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
              />
            </>
          )}
        </div>

        {/* Échelle d'amplitude */}
        <canvas
          ref={amplitudeSliderRef}
          style={{
            width: `${amplitudeSliderWidth}px`,
            height: bottomBarHeight - timelineHeight - controlBarHeight,
            cursor: 'ns-resize',
            boxSizing: 'border-box',
          }}
        />
      </div>
    </div>
  );
}
